import { ApiQueryName } from "@/models/api"
import type { Organization } from "@/models/organization"
import type {
  BillChargeLineListResponse,
  ChargeLineResponse,
} from "@/services/core-api/types"
import { useQueryKey, useRenameQueryResult } from "@/services/utils"
import type { HttpError } from "@/utils/api"
import { httpClient, toHttpError } from "@/utils/api"
import { fetchAllPages } from "@/utils/fetch-all-pages"
import { useQuery } from "@tanstack/react-query"
import type { UseQueryOptions } from "@tanstack/react-query"

const BILL_LINE_ITEMS_PAGE_LIMIT = 100
const BILL_LINE_ITEMS_MAX_PAGES = 100

interface FetchBillLineItemsParams {
  billId: number
  offset: number
  organizationId: Organization["id"]
}

const fetchBillLineItemsPage = async ({
  billId,
  offset,
  organizationId,
}: FetchBillLineItemsParams): Promise<BillChargeLineListResponse> => {
  try {
    const response = await httpClient.get<BillChargeLineListResponse>(
      `/api/v1/organizations/${organizationId}/bills/${billId}/line-items`,
      {
        params: {
          limit: BILL_LINE_ITEMS_PAGE_LIMIT,
          offset,
        },
      }
    )

    return response.data
  } catch (error: unknown) {
    throw toHttpError(error)
  }
}

const sortChargeLines = (lines: ChargeLineResponse[]): ChargeLineResponse[] =>
  [...lines].sort((left, right) => left.sort_order - right.sort_order)

const fetchAllBillLineItems = async ({
  billId,
  organizationId,
}: Omit<FetchBillLineItemsParams, "offset">): Promise<ChargeLineResponse[]> =>
  sortChargeLines(
    await fetchAllPages({
      fetchPage: (offset) =>
        fetchBillLineItemsPage({ billId, offset, organizationId }),
      incompleteError: (received, total) =>
        toHttpError(
          new Error(
            `Bill ${billId} line items incomplete: received ${received} of ${total}`
          )
        ),
      isEmptyResult: (page) => !page.itemized,
      maxPages: BILL_LINE_ITEMS_MAX_PAGES,
    })
  )

interface UseBillLineItemsOptions {
  billId: number | null
  organizationId: Organization["id"]
  queryOptions?: Omit<
    UseQueryOptions<ChargeLineResponse[], HttpError>,
    "queryKey" | "queryFn"
  >
}

export const useBillLineItems = ({
  billId,
  organizationId,
  queryOptions = {},
}: UseBillLineItemsOptions) => {
  const queryKey = useQueryKey(
    ApiQueryName.BillLineItems,
    "getMany",
    billId === null ? null : String(billId),
    {
      organizationId,
    }
  )

  const { enabled, ...restQueryOptions } = queryOptions

  const queryResult = useQuery<ChargeLineResponse[], HttpError>({
    ...restQueryOptions,
    enabled: Boolean(organizationId) && billId !== null && (enabled ?? true),
    queryFn: async () => {
      if (billId === null) {
        throw new Error("billId is required to fetch bill line items")
      }

      return fetchAllBillLineItems({ billId, organizationId })
    },
    queryKey,
    throwOnError: false,
  })

  return useRenameQueryResult(queryResult, ApiQueryName.BillLineItems)
}
